import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { API_URL } from "../config";
import { FaPrint } from "react-icons/fa";
import usePageMeta from "../hooks/usePageMeta";
import { pageTitle } from "../lib/seo";
import { useBrand } from "../brand/BrandContext";
import "./VoucherView.css";

function formatDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-IN", { year: "numeric", month: "long", day: "numeric" });
}

function formatTime(t) {
  if (!t) return "";
  const [h, m] = t.split(":");
  const hour = Number(h);
  const period = hour >= 12 ? "PM" : "AM";
  const hour12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${hour12}:${m} ${period}`;
}

export default function VoucherView() {
  const brand = useBrand();
  const COMPANY = {
    name: brand.name,
    tagline: brand.parentCompany ? `A part of ${brand.parentCompany}` : brand.tagline,
    phone: brand.phone,
    email: brand.email,
  };
  const { slug } = useParams();
  const [state, setState] = useState({ loading: true, voucher: null, error: null });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Vouchers aren't publicly readable in the database; the backend
        // looks this one up by its private slug.
        const res = await fetch(`${API_URL}/api/public/vouchers/${encodeURIComponent(slug)}`);

        if (cancelled) return;
        if (res.status === 404) { setState({ loading: false, voucher: null, error: "not_found" }); return; }
        if (!res.ok) throw new Error(`Voucher request failed (${res.status})`);
        const { voucher } = await res.json();
        setState({ loading: false, voucher, error: null });
      } catch (err) {
        console.error("VoucherView error:", err);
        if (!cancelled) setState({ loading: false, voucher: null, error: "error" });
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  const v = state.voucher;

  usePageMeta({
    title: v ? pageTitle(`Hotel Voucher — ${v.hotel_name || "Booking"}`) : undefined,
    description: v ? `Voucher for ${v.guest_name ? `${v.guest_name} at ` : ""}${v.hotel_name || "your hotel"}${v.hotel_country ? `, ${v.hotel_country}` : ""}.` : undefined,
    image: v?.hotel_image,
    noindex: true,
  });

  if (state.loading) return <div className="voucher-loading">Loading voucher…</div>;
  if (state.error === "not_found") return <ErrorScreen heading="Voucher not found" body="The link you followed is invalid or has been removed." />;
  if (state.error) return <ErrorScreen heading="Something went wrong" body="Please try again in a moment." />;

  const inclusions = (v.room_inclusions || "").split("\n").map((s) => s.trim()).filter(Boolean);
  const checkIn = [formatDate(v.check_in_date), formatTime(v.check_in_time)].filter(Boolean).join(" — ");
  const checkOut = [formatDate(v.check_out_date), formatTime(v.check_out_time)].filter(Boolean).join(" — ");

  return (
    <div className="voucher-page">
      <div className="voucher-toolbar no-print">
        <button onClick={() => window.print()} className="voucher-print-btn" data-testid="voucher-print-btn">
          <FaPrint /> Print / Save as PDF
        </button>
      </div>

      <div className="voucher-sheet">
        <header className="voucher-header">
          <div className="voucher-company">
            <h1>{COMPANY.name}</h1>
            <p className="voucher-tagline">{COMPANY.tagline}</p>
            <p>{COMPANY.email}</p>
            <p>Contact: {COMPANY.phone}</p>
          </div>
          <div className="voucher-doc-tag">HOTEL VOUCHER</div>
        </header>

        <section className="voucher-top-meta">
          <div>
            <span className="voucher-label">Issued Date</span>
            <span className="voucher-value">{formatDate(v.issued_date)}</span>
          </div>
          <div>
            <span className="voucher-label">Booking Reference Number</span>
            <span className="voucher-value">{v.booking_reference || "—"}</span>
          </div>
        </section>

        {v.guest_name && (
          <section className="voucher-guest">
            <span className="voucher-label">Guest Name</span>
            <span className="voucher-value voucher-value-lg">{v.guest_name}</span>
          </section>
        )}

        <section className="voucher-block">
          <h2>Hotel Details</h2>
          {v.hotel_image && (
            <img src={v.hotel_image} alt={v.hotel_name || "Hotel"} className="voucher-hotel-image" />
          )}
          <div className="voucher-grid">
            <div><span className="voucher-label">Hotel Name</span><span className="voucher-value">{v.hotel_name || "—"}</span></div>
            <div><span className="voucher-label">Hotel Country</span><span className="voucher-value">{v.hotel_country || "—"}</span></div>
            <div className="voucher-span-2"><span className="voucher-label">Hotel Address</span><span className="voucher-value">{v.hotel_address || "—"}</span></div>
            <div><span className="voucher-label">Hotel Contact Number</span><span className="voucher-value">{v.hotel_contact_number || "—"}</span></div>
            <div><span className="voucher-label">Hotel Email Address</span><span className="voucher-value">{v.hotel_email || "—"}</span></div>
          </div>
        </section>

        <section className="voucher-block">
          <h2>Room & Stay Details</h2>
          <div className="voucher-grid">
            <div><span className="voucher-label">Room Type</span><span className="voucher-value">{v.room_type || "—"}</span></div>
            <div><span className="voucher-label">Number of Rooms</span><span className="voucher-value">{v.number_of_rooms ?? "—"}</span></div>
            <div><span className="voucher-label">Check In</span><span className="voucher-value">{checkIn || "—"}</span></div>
            <div><span className="voucher-label">Check Out</span><span className="voucher-value">{checkOut || "—"}</span></div>
          </div>
          {inclusions.length > 0 && (
            <div className="voucher-inclusions">
              <span className="voucher-label">Room Inclusions</span>
              <ul>
                {inclusions.map((i, idx) => <li key={idx}>{i}</li>)}
              </ul>
            </div>
          )}
        </section>

        {v.terms_and_conditions && (
          <section className="voucher-block">
            <h2>Terms and Conditions</h2>
            <p className="voucher-terms">{v.terms_and_conditions}</p>
          </section>
        )}

        <footer className="voucher-footer">
          <p>{COMPANY.name} ({COMPANY.tagline}) • {COMPANY.email} • {COMPANY.phone}</p>
          <p className="voucher-ref">Voucher reference: {slug}</p>
        </footer>
      </div>
    </div>
  );
}

function ErrorScreen({ heading, body }) {
  return (
    <div className="voucher-empty">
      <h1>{heading}</h1>
      <p>{body}</p>
      <Link to="/" className="voucher-home-link">Visit our website</Link>
    </div>
  );
}
